/** 海康威视摄像头 — 智能设备管理辅助（海康互联云端抓拍） */

import { TYPE_CAMERA, isCameraType } from './cameraDeviceType';

export const MANUFACTURER_HIKVISION = '海康威视';
export const PLATFORM_HIKVISION = 'HIKVISION';
export const DEFAULT_HIKVISION_PROXY_NAME = 'field-hikvision-nvr';
export { TYPE_CAMERA };

/** videoLevel：0流畅 1均衡 2高清 3超清 4极清 */
export const VIDEO_LEVEL_LABELS = {
    0: '流畅',
    1: '均衡',
    2: '高清',
    3: '超清',
    4: '极清'
};

export function videoLevelLabel(level) {
    const n = Number(level);
    if (!Number.isFinite(n)) return '未知';
    return VIDEO_LEVEL_LABELS[n] || '未知';
}

export function isHikvisionManufacturer(name) {
    const s = String(name || '');
    return s.includes('海康') || s.toLowerCase().includes('hikvision') || s.toLowerCase().includes('hik');
}

export function isHikvisionCamera(device = {}) {
    if (device == null || typeof device !== 'object') return false;
    if (String(device.thirdPartyPlatform || '').toUpperCase() === PLATFORM_HIKVISION) return true;
    if (isHikvisionManufacturer(device.manufacturer)) return true;
    const type = String(device.deviceType || '').toUpperCase();
    // 极飞农田相机也映射为 CAMERA，需排除；海康侧兼容历史 IP_CAMERA
    if (!isCameraType(type) || type === 'FARM_CAMERA' || type === 'FC5_CAMERA') return false;
    if (String(device.manufacturer || '').includes('极飞')) return false;
    if (device.jifeiDeviceId || device.jifeiModelType != null) return false;
    const cfg = parseHikvisionConfig(device);
    return Boolean(cfg.deviceSerial);
}

export function parseJsonSafe(raw) {
    if (!raw) return {};
    if (typeof raw === 'object') return { ...raw };
    try {
        const p = JSON.parse(raw);
        return p && typeof p === 'object' ? p : {};
    } catch {
        return {};
    }
}

/** 通道：NVR 通道号从 1 起；兼容旧 ISAPI 码流号 101 → 通道 1。不是球机 IP。 */
export function normalizeChannelNo(value, fallback = 1) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    if (n >= 100) return Math.max(1, Math.floor(n / 100));
    return Math.floor(n);
}

/** 田间机局域网末位：通道 n → 100+n（仅相对该机网段，不是全局 IP） */
export const NVR_CHANNEL_IP_BASE = 100;

export function normalizeLanPrefix(raw) {
    let s = String(raw || '').trim();
    if (!s) return '';
    s = s.replace(/\/+$/, '');
    if (!s.endsWith('.')) s += '.';
    return s;
}

export function parseHikvisionNvrHostConfig(device = {}) {
    const root = parseJsonSafe(device.configJson);
    const nvr = root.hikvisionNvr && typeof root.hikvisionNvr === 'object' ? root.hikvisionNvr : {};
    const proxyName = String(nvr.proxyName || nvr.serverName || nvr.tunnelCode || '').trim();
    return {
        lanPrefix: normalizeLanPrefix(nvr.lanPrefix || nvr.nvrCameraIpPrefix || ''),
        proxyName,
        tunnelCode: String(nvr.tunnelCode || proxyName || '').trim()
    };
}

export function buildHikvisionNvrHostConfigJson(fields = {}, existingConfigJson) {
    const root = parseJsonSafe(existingConfigJson);
    const prev = root.hikvisionNvr && typeof root.hikvisionNvr === 'object' ? { ...root.hikvisionNvr } : {};
    const lanPrefix = normalizeLanPrefix(
        fields.lanPrefix != null ? fields.lanPrefix : (prev.lanPrefix || prev.nvrCameraIpPrefix || '')
    );
    const proxyName = String(
        fields.proxyName != null ? fields.proxyName : (prev.proxyName || prev.serverName || '')
    ).trim();
    const tunnelCode = String(
        fields.tunnelCode != null ? fields.tunnelCode : (prev.tunnelCode || proxyName || '')
    ).trim();
    root.hikvisionNvr = { ...prev, lanPrefix, proxyName, tunnelCode };
    return JSON.stringify(root);
}

/** 解析多通道列表：channels 数组 / 逗号串，并保证包含默认 channelNo */
export function normalizeChannelList(rawChannels, defaultChannelNo = 1) {
    const out = [];
    const push = (v) => {
        const n = normalizeChannelNo(v, 0);
        if (n > 0 && !out.includes(n)) out.push(n);
    };
    if (Array.isArray(rawChannels)) {
        rawChannels.forEach(push);
    } else if (rawChannels != null && String(rawChannels).trim()) {
        String(rawChannels).split(/[,，\s]+/).forEach(push);
    }
    const def = normalizeChannelNo(defaultChannelNo, 1);
    if (!out.includes(def)) out.unshift(def);
    out.sort((a, b) => a - b);
    return out;
}

/** 解析 configJson.hikvision */
export function parseHikvisionConfig(device = {}) {
    const root = parseJsonSafe(device.configJson);
    const hik = root.hikvision && typeof root.hikvision === 'object' ? root.hikvision : {};
    const deviceSerial = String(
        hik.deviceSerial || hik.serial || hik.deviceSn || hik.sn || ''
    ).trim();
    const channelNo = normalizeChannelNo(
        hik.channelNo != null && hik.channelNo !== '' ? hik.channelNo : (hik.channelId || hik.channel || 1),
        1
    );
    const channels = normalizeChannelList(hik.channels, channelNo);
    return {
        deviceSerial,
        channelNo,
        channels,
        channelId: String(channelNo),
        nvrDeviceId: String(hik.nvrDeviceId || hik.fieldGatewayId || '').trim(),
        proxyName: String(hik.proxyName || hik.serverName || hik.tunnelCode || '').trim(),
        associatedMapZoneIndexes: normalizeAssociatedMapZoneIndexes(hik.associatedMapZoneIndexes),
        rtspUrl: hik.rtspUrl != null ? String(hik.rtspUrl) : '',
        streamUrl: hik.streamUrl != null ? String(hik.streamUrl) : '',
        schedules: Array.isArray(hik.schedules) ? hik.schedules : [],
        capturePlan: hik.capturePlan && typeof hik.capturePlan === 'object' ? hik.capturePlan : null,
        captureRuntime: hik.captureRuntime && typeof hik.captureRuntime === 'object' ? hik.captureRuntime : null,
        capacities: hik.capacities && typeof hik.capacities === 'object' ? hik.capacities : null,
        defaultPresetId: Number(hik.defaultPresetId) > 0 ? Number(hik.defaultPresetId) : 0,
        parkEnabled: hik.parkEnabled === true || hik.parkEnabled === 1 || hik.parkEnabled === 'true' || hik.parkEnabled === '1',
        homePose: hik.homePose && typeof hik.homePose === 'object' ? hik.homePose : null
    };
}

/** 能力集 support*=1 的中文标签（其余去掉 support 前缀展示） */
export const HIKVISION_CAPACITY_LABELS = {
    supportPreview: '预览',
    supportRecord: '录像',
    supportCapture: '抓拍',
    supportTalk: '对讲',
    supportPtz: '云台',
    supportVideoBack: '回放',
    supportDownload: '下载',
    supportAlarmPic: '报警图',
    supportAlarmMessage: '报警消息',
    supportAlarmMessageDetection: '报警检测',
    supportDeviceSetting: '设备设置',
    supportUpgrade: '升级',
    supportLocalStorage: '本地存储',
    supportShare: '分享',
    supportIsapi: 'ISAPI',
    supportLightSwitch: '灯光',
    supportSoundSwitch: '声音',
    supportShadow: '物模型',
    supportLink: '联动',
    supportGL: '国标',
    supportSmartHumanCar: '人车检测',
    supportLowPower: '低功耗',
    supportAuth: '权限',
    supportDeviceVersion: '设备版本',
    supportRecordPage: '录像页',
    supportVideoQuality: '画质',
    supportPhysicalAdd: '物理添加',
    supportDefaultMode: '默认模式',
    supportVideoBackDetection: '回放检测',
    supportPhysicalModel: '物理型号',
    supportPlayQuickOpt: '快放',
    supportPlaySlowOpt: '慢放'
};

export function hikvisionCapacityLabel(key) {
    if (HIKVISION_CAPACITY_LABELS[key]) return HIKVISION_CAPACITY_LABELS[key];
    const s = String(key || '');
    return s.startsWith('support') ? s.slice('support'.length) : s;
}

export function hikvisionEnabledCapacities(device = {}) {
    const cap = parseHikvisionConfig(device).capacities;
    if (!cap) return [];
    if (Array.isArray(cap.enabled) && cap.enabled.length) {
        return cap.enabled.map(String);
    }
    const raw = cap.raw && typeof cap.raw === 'object' ? cap.raw : {};
    const keys = [];
    const collect = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        Object.entries(obj).forEach(([k, v]) => {
            if (!String(k).startsWith('support')) return;
            const on = v === 1 || v === '1' || v === true;
            if (on && !keys.includes(k)) keys.push(k);
        });
    };
    collect(raw);
    collect(raw.authMap);
    return keys;
}

/** 地块地图「选区」下标（与 /production/land/:id/map 外边界选区 1..N 对齐，0 起） */
export function normalizeAssociatedMapZoneIndexes(raw) {
    const out = [];
    const push = (v) => {
        const i = parseInt(String(v), 10);
        if (!Number.isFinite(i) || i < 0 || out.includes(i)) return;
        out.push(i);
    };
    if (Array.isArray(raw)) {
        raw.forEach(push);
    } else if (raw != null && String(raw).trim()) {
        String(raw).split(/[,，\s]+/).forEach(push);
    }
    out.sort((a, b) => a - b);
    return out;
}

export function mapZoneRecordId(index) {
    return `map-zone-${index}`;
}

export function parseMapZoneRecordId(id) {
    const m = String(id || '').match(/^map-zone-(\d+)$/);
    if (!m) return null;
    const i = Number(m[1]);
    return Number.isFinite(i) ? i : null;
}

export function matchesLiveHikvisionProxy(proxyName) {
    return String(proxyName || '').trim() === DEFAULT_HIKVISION_PROXY_NAME;
}

export function isCameraConfigured(device = {}) {
    const c = parseHikvisionConfig(device);
    return Boolean(c.deviceSerial && c.channelNo > 0 && matchesLiveHikvisionProxy(c.proxyName));
}

export function buildHikvisionConfigJson(fields = {}, existingConfigJson) {
    const root = parseJsonSafe(existingConfigJson);
    const prev = root.hikvision && typeof root.hikvision === 'object' ? { ...root.hikvision } : {};
    const deviceSerial = String(
        fields.deviceSerial || fields.serial || prev.deviceSerial || prev.serial || ''
    ).trim();
    const channelNo = normalizeChannelNo(
        fields.channelNo != null && fields.channelNo !== ''
            ? fields.channelNo
            : (fields.channelId || fields.channel || prev.channelNo || prev.channelId || 1),
        1
    );
    const channels = normalizeChannelList(
        fields.channels != null ? fields.channels : prev.channels,
        channelNo
    );
    // 设备级动作链；与租户 IoT 抓拍 cron 分存。未传入则保留，避免改其它字段冲掉。
    const schedules = fields.schedules != null
        ? fields.schedules
        : (Array.isArray(prev.schedules) ? prev.schedules : []);
    const proxyName = String(
        fields.proxyName != null ? fields.proxyName : (prev.proxyName || prev.serverName || prev.tunnelCode || '')
    ).trim();
    root.hikvision = {
        ...prev,
        deviceSerial,
        channelNo,
        channels,
        channelId: String(channelNo),
        proxyName,
        serverName: proxyName,
        tunnelCode: proxyName || String(prev.tunnelCode || '').trim(),
        schedules,
        // 表单已隐藏 RTSP/直播地址：未传入则保留，避免保存冲掉
        rtspUrl: fields.rtspUrl != null ? String(fields.rtspUrl).trim() : (prev.rtspUrl || ''),
        streamUrl: fields.streamUrl != null ? String(fields.streamUrl).trim() : (prev.streamUrl || ''),
        associatedMapZoneIndexes: fields.associatedMapZoneIndexes != null
            ? normalizeAssociatedMapZoneIndexes(fields.associatedMapZoneIndexes)
            : normalizeAssociatedMapZoneIndexes(prev.associatedMapZoneIndexes)
    };
    delete root.hikvision.associatedPartitionIds;
    delete root.hikvision.host;
    delete root.hikvision.ip;
    delete root.hikvision.port;
    delete root.hikvision.useHttps;
    delete root.hikvision.username;
    delete root.hikvision.user;
    delete root.hikvision.password;
    delete root.hikvision.pass;
    delete root.hikvision.fieldGatewayId;
    if (!proxyName) {
        delete root.hikvision.proxyName;
        delete root.hikvision.serverName;
        delete root.hikvision.tunnelCode;
    }
    return JSON.stringify(root);
}

/** 设备级田间抓拍计划（与动作链 schedules 分开）；间隔固定 3 小时，界面不提供改间隔。 */
export const CAPTURE_INTERVAL_MINUTES = 180;

export function clampCaptureIntervalMinutes(v, fallback = CAPTURE_INTERVAL_MINUTES) {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(1440, Math.max(5, Math.round(n)));
}

export function normalizeHikvisionCapturePlan(raw = {}) {
    const src = raw && typeof raw === 'object' ? raw : {};
    return {
        enabled: src.enabled !== false,
        dayStart: String(src.dayStart || '06:00').slice(0, 5),
        dayEnd: String(src.dayEnd || '19:00').slice(0, 5),
        intervalMinutes: CAPTURE_INTERVAL_MINUTES,
        nightEnabled: src.nightEnabled === true,
        nightStart: String(src.nightStart || '19:00').slice(0, 5),
        nightEnd: String(src.nightEnd || '06:00').slice(0, 5),
        nightIntervalMinutes: CAPTURE_INTERVAL_MINUTES,
        aiAnalyzeEnabled: src.aiAnalyzeEnabled !== false,
        aiMinIntervalMinutes: CAPTURE_INTERVAL_MINUTES,
        dayWindowMode: ['fixed', 'schedule', 'manual'].includes(String(src.dayWindowMode || '').toLowerCase())
            ? 'fixed'
            : 'sun',
        sunHourMode: String(src.sunHourMode || 'before').toLowerCase() === 'after' ? 'after' : 'before',
        stageRhythmCrop: String(src.stageRhythmCrop || '').trim(),
        stageRhythms: Array.isArray(src.stageRhythms)
            ? src.stageRhythms.filter((row) => row && (row.stageKey || row.stageName))
            : []
    };
}

export function captureIntervalPresetValue() {
    return CAPTURE_INTERVAL_MINUTES;
}

